import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useCashbackTransactionDetails from 'features/rewards/hooks/useCashbackTransactionDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const cashBackList = [
  'TITLE_',
  'AMOUNT_PURCHASED',
  'TRANSACTION_DATE_',
  'CASHBACK_STATUS_',
  'DESCRIPTION_',
];

const transactionList = [
  'TRANSACTION_DATE_',
  'POSTED_DATE_',
  'TRANSACTION_STATUS_',
  'DESCRIPTION_',
];

const CashbackTransactionDetailsScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const { transactionId } = useLocalSearchParams();

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'cashbackTransactionDetails',
  });

  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();

  const { loading, error, cashbackTransactionDetails, stale } =
    useCashbackTransactionDetails(transactionId.toString());

  const isCashback = cashbackTransactionDetails?.isCashbackTransaction;

  const detailItems = cashbackTransactionDetails?.details?.[0]?.items;

  const detailKeys = isCashback ? cashBackList : transactionList;

  function findDetails(key: string) {
    const detail = detailItems?.find((item) => {
      return item.key.startsWith(key);
    });
    if (!detail) {
      return {
        key: 'Description',
        title: translatedText('description'),
        value: cashbackTransactionDetails?.description,
      };
    }

    return detail;
  }

  const details = detailKeys.map((key) => findDetails(key));

  const cashbackAmountFormatted =
    Number(cashbackTransactionDetails?.cashbackAmount?.amount) > 0
      ? cashbackTransactionDetails?.cashbackAmount?.formattedWithPlusPrefix
      : cashbackTransactionDetails?.cashbackAmount?.formatted;

  const handleBackArrowNavigation = () => {
    router.back();
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: isCashback
            ? translatedText('cashbackTitle')
            : translatedText('transactionTitle'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackArrowNavigation}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading || stale}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: () => router.navigate('/rewards'),
              }}
              screenAnalyticsEventKey="rewardsError"
            />
          ) : null
        }
      />
      {detailItems && detailItems.length > 0 ? (
        <ScrollView contentContainerStyle={styles.scrollView}>
          <View style={styles.cashbackContainer}>
            <ExpressiveSmText
              style={styles.centeredText}
              testID="cashbackTrnxAmount"
            >
              {cashbackAmountFormatted}
            </ExpressiveSmText>
          </View>
          {details.map((detail, index) => (
            <View key={detail.key} style={styles.lineContainer}>
              <View style={styles.lineInnerContainer}>
                <BodyXSText>{detail.title}</BodyXSText>
                <BodyBaseText>{detail.value}</BodyBaseText>
              </View>
              {index !== details.length - 1 ? <HairLineDivider /> : null}
            </View>
          ))}
        </ScrollView>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  cashbackContainer: {
    padding: theme.spacing.m,
  },
  centeredText: {
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  lineContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  lineInnerContainer: {
    paddingVertical: theme.spacing.m,
  },

  scrollView: { gap: theme.spacing.s },
});

export default CashbackTransactionDetailsScreen;
